export type AccessTier = 'GLOBAL' | 'DEPARTMENT' | 'ASSIGNED' | 'PERSONAL';

export type DraftStatus =
  | 'AI_DRAFT'
  | 'EDITED'
  | 'REVIEW'
  | 'APPROVED'
  | 'READY_TO_SEND'
  | 'SENT'
  | 'REJECTED';

export type TicketStatus = 'WAITING_OWNER' | 'OWNER_ANSWERED' | 'CLOSED';

export type KnowledgeSourceStatus =
  | 'DRAFT'
  | 'PROCESSING'
  | 'INDEXED'
  | 'ACTIVE'
  | 'OUTDATED'
  | 'ARCHIVED'
  | 'ERROR';

export interface ScopeSnapshot {
  access_tier: AccessTier;
  active_role: string;
  department_id: string | null;
  allowed_collections: string[];
  allowed_actions: string[];
}

export interface SourceRef {
  title: string;
  collection: string;
  status: string;
  last_verified: string;
  url?: string | null;
  file_id?: string;
  version_no?: string;
  version_id?: string;
  citation_anchor?: string;
  excerpt_text?: string;
}

export interface AIConversation {
  id: string;
  active_role: string;
  scope_snapshot: ScopeSnapshot;
  title: string;
  context_mode: string;
  created_at: string;
  updated_at: string;
}

export interface AIMessage {
  id: string;
  conversation_id: string;
  actor: 'USER' | 'AI' | 'OWNER';
  text: string;
  source_refs: SourceRef[];
  draft_id: string | null;
  created_at: string;
}

export interface AIDraft {
  id: string;
  conversation_id: string | null;
  action_type: string;
  target_module: string;
  payload: Record<string, unknown>;
  status: DraftStatus;
  created_by: string;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface RecipientItem {
  user_id: string;
  full_name: string;
  email: string;
  role: string;
  department: string | null;
  course_year: number | null;
  study_program: string | null;
}

export interface RecipientResolveResult {
  count: number;
  recipients: RecipientItem[];
  filter_options: {
    departments: string[];
    courses: number[];
    programs: string[];
  };
}

export interface AITicket {
  id: string;
  ticket_code: string;
  origin_account_id: string;
  origin_user_name: string;
  origin_role: string;
  question: string;
  explanation: string;
  status: TicketStatus;
  owner_answer: string | null;
  answered_by: string | null;
  answered_at: string | null;
  conversation_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface KnowledgeSource {
  id: string;
  collection: string;
  title: string;
  category: string;
  language: string;
  access_scope: string;
  department_id: string | null;
  version: string;
  status: KnowledgeSourceStatus;
  content_summary: string;
  source_url: string | null;
  valid_from: string | null;
  valid_to: string | null;
  last_verified_at: string;
  created_at: string;
  updated_at: string;
}

export interface ChatMessageResult {
  user_message: AIMessage;
  ai_message: AIMessage;
  draft: AIDraft | null;
}

export interface ContextualPromptResult {
  suggested_text: string;
  action_type: string;
  requires_approval: boolean;
}

export interface JournalSyncStatus {
  incremental_sync_interval: string;
  next_reconciliation: string;
  last_verified_at: string;
  total_journals: number;
  status: string;
}

export const CANONICAL_COLLECTIONS = [
  { id: '01_PLATFORM_KNOWLEDGE', label: 'Платформа' },
  { id: '02_REGULATORY_ACADEMIC', label: 'Нормативная база' },
  { id: '03_JOURNALS_AND_INDEXES', label: 'Журналы и индексы' },
  { id: '04_DOCTORAL_PROCESS', label: 'Процесс докторантуры' },
  { id: '05_DEPARTMENT_KNOWLEDGE', label: 'База кафедры' },
  { id: '06_USER_PRIVATE_DATA', label: 'Мои данные' },
  { id: '07_OWNER_ADMIN_KNOWLEDGE', label: 'Администрирование' },
] as const;

export const CONTEXT_FILTERS: Record<AccessTier, string[]> = {
  GLOBAL: [
    'Вся система',
    'Пользователи',
    'Докторанты',
    'Подразделения',
    'Публикации',
    'Документы',
    'Журналы',
    'Нормативная база',
  ],
  DEPARTMENT: [
    'Моё подразделение',
    'Докторанты',
    'Публикации',
    'Аттестация',
    'Стажировки',
    'Предзащиты',
    'Защиты',
    'Документы кафедры',
    'Журналы',
  ],
  ASSIGNED: [
    'Мои подопечные',
    'Назначенные проекты',
    'Публикации',
    'Документы',
    'Журналы',
  ],
  PERSONAL: [
    'Мои данные',
    'Мой учебный процесс',
    'Мои публикации',
    'Мои документы',
    'Мои сроки',
    'Предзащита',
    'Защита',
    'Журналы',
    'Образцы',
  ],
};

export const QUICK_ACTIONS: Record<AccessTier, string[]> = {
  GLOBAL: ['Создать', 'Написать', 'Проанализировать', 'Найти', 'Организовать'],
  DEPARTMENT: ['Создать', 'Написать', 'Проанализировать', 'Найти'],
  ASSIGNED: ['Написать', 'Проанализировать', 'Найти'],
  PERSONAL: ['Написать', 'Проанализировать', 'Найти'],
};

export const SCOPE_LABELS: Record<AccessTier, string> = {
  GLOBAL: 'GLOBAL ACCESS',
  DEPARTMENT: 'DEPARTMENT ACCESS',
  ASSIGNED: 'ASSIGNED ACCESS',
  PERSONAL: 'PERSONAL ACCESS',
};
